import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { PreferencesList } from '@/components/PreferencesList';
import { ReferencePhotos } from '@/components/BookingExtras';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Scissors, Clock, Star, MapPin, Phone, ChevronRight, Calendar, AlertCircle, Edit, Loader2, XCircle, CalendarPlus } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useClientAuth } from '@/context/ClientAuthContext';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const { client, getAuthHeaders } = useClientAuth();
  const [nextAppointment, setNextAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [siteTexts, setSiteTexts] = useState(null);
  const [dbServices, setDbServices] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [pendingBookings, setPendingBookings] = useState([]);
  const [operators, setOperators] = useState([]);
  const [cancelBooking, setCancelBooking] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  // Smart calendar link: iOS → ICS (opens Apple Calendar), Android/Desktop → Google Calendar
  const getCalendarUrl = (booking) => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      return `${API}/bookings/${booking.id}/calendar`;
    }
    // Google Calendar URL
    const dateStr = (booking.date || '').replace(/-/g, '');
    const [h, m] = (booking.time || '10:00').split(':');
    const startT = `${dateStr}T${h}${m}00`;
    const endH = String(Number(h) + (Number(m) + 30 >= 60 ? 1 : 0)).padStart(2, '0');
    const endM = String((Number(m) + 30) % 60).padStart(2, '0');
    const endT = `${dateStr}T${endH}${endM}00`;
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent('Appuntamento - CAPELLI & CAPELLI')}&dates=${startT}/${endT}&ctz=Europe/Rome&details=${encodeURIComponent(`Servizio: ${booking.service_name || ''}`)}&sf=true&output=xml`;
  };

  const features = [
    { icon: Star, title: '4.9/5', description: 'Valutazione clienti' },
    { icon: Scissors, title: 'Specialista', description: 'Tagli maschili' },
    { icon: MapPin, title: 'Professionale', description: 'Salone moderno' }
  ];

  useEffect(() => {
    fetchNextAppointment();
    fetchSiteTexts();
    fetchServices();
    fetchOperators();
  }, []);

  const fetchSiteTexts = async () => {
    try {
      const response = await axios.get(`${API}/site-texts`);
      setSiteTexts(response.data);
    } catch (error) { console.error('Failed to fetch site texts:', error); }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setDbServices(response.data);
    } catch (error) { console.error('Failed to fetch services:', error); }
  };

  const fetchOperators = async () => {
    try {
      const response = await axios.get(`${API}/operators/public`);
      setOperators(response.data);
    } catch (error) { console.error('Failed to fetch operators:', error); }
  };

  const fetchNextAppointment = async () => {
    try {
      const [nextRes, allRes] = await Promise.all([
        axios.get(`${API}/bookings/next-appointment`, { headers: getAuthHeaders() }),
        axios.get(`${API}/bookings/my-bookings`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
      ]);
      setNextAppointment(nextRes.data);
      const today = new Date().toISOString().split('T')[0];
      setPendingBookings(allRes.data.filter(b => b.status === 'pending' && b.date >= today));
    } catch (error) {
      // No appointment is fine
    } finally {
      setLoading(false);
    }
  };

  const openEditBooking = (booking) => {
    setEditForm({
      id: booking.id,
      service_name: booking.service_name,
      operator_name: booking.operator_name || '',
      date: booking.date,
      time: booking.time,
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/bookings/my-bookings/${editForm.id}`, {
        service_name: editForm.service_name,
        operator_name: editForm.operator_name || undefined,
        date: editForm.date,
        time: editForm.time,
      }, { headers: getAuthHeaders() });
      toast.success('Prenotazione aggiornata! In attesa di conferma.');
      setEditOpen(false);
      fetchNextAppointment();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella modifica');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!cancelBooking) return;
    setCancelling(true);
    try {
      await axios.put(`${API}/bookings/my-bookings/${cancelBooking.id}`, {
        status: 'cancelled'
      }, { headers: getAuthHeaders() });
      toast.success('Appuntamento annullato');
      setCancelBooking(null);
      fetchNextAppointment();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'annullamento');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Appointment Reminders */}
      {!loading && (nextAppointment || pendingBookings.length > 0) && (
        <div className="bg-accent/10 border-y border-accent/20 py-4">
          <div className="container mx-auto px-4 space-y-3">
            {/* Confirmed appointment */}
            {nextAppointment && (
              <Card className="card-elegant bg-accent/5 border-accent/30" data-testid="next-appointment-card">
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2.5 rounded-lg bg-accent/20 flex-shrink-0">
                      <Calendar className="h-5 w-5 text-accent" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold mb-2">Prossimo Appuntamento</h3>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><span className="text-muted-foreground text-xs block">Servizio</span><p className="font-semibold">{nextAppointment.service_name}</p></div>
                        <div><span className="text-muted-foreground text-xs block">Data</span><p className="font-semibold">{format(new Date(nextAppointment.date), 'dd MMM yyyy', { locale: it })}</p></div>
                        <div><span className="text-muted-foreground text-xs block">Orario</span><p className="font-semibold">{nextAppointment.time}</p></div>
                        <div><span className="text-muted-foreground text-xs block">Stato</span><p className="font-semibold text-accent">Confermato</p></div>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <a href={getCalendarUrl(nextAppointment)} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-green-500/40 bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors"
                        data-testid="calendar-next-appointment">
                        <CalendarPlus className="h-3.5 w-3.5" /> Calendario
                      </a>
                      <Button variant="outline" size="sm" onClick={() => openEditBooking(nextAppointment)} data-testid="edit-next-appointment">
                        <Edit className="h-3.5 w-3.5 mr-1" /> Modifica
                      </Button>
                      <Button variant="outline" size="sm" className="text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30" onClick={() => setCancelBooking(nextAppointment)} data-testid="cancel-next-appointment">
                        <XCircle className="h-3.5 w-3.5 mr-1" /> Annulla
                      </Button>
                    </div>
                  </div>
                  <PreferencesList preferences={nextAppointment.client_preferences} compact title="Le tue preferenze" testId="next-appointment-preferences" />
                  <ReferencePhotos booking={nextAppointment} headers={getAuthHeaders()} title="Le tue foto di riferimento" />
                </CardContent>
              </Card>
            )}
            {/* Pending bookings */}
            {pendingBookings.length > 0 && pendingBookings.map(booking => (
              <Card key={booking.id} className="card-elegant bg-yellow-500/5 border-yellow-500/30" data-testid={`pending-booking-${booking.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-yellow-500/20 flex-shrink-0">
                      <Clock className="h-4 w-4 text-yellow-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold">{booking.service_name} — <span className="text-yellow-500">In attesa di conferma</span></p>
                      <p className="text-xs text-muted-foreground">{format(new Date(booking.date), 'dd MMM yyyy', { locale: it })} alle {booking.time}</p>
                    </div>
                    <div className="flex gap-1.5 flex-shrink-0">
                      <a href={getCalendarUrl(booking)} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-green-500/40 bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors"
                        data-testid={`calendar-pending-${booking.id}`}>
                        <CalendarPlus className="h-3 w-3" />
                      </a>
                      <Button variant="outline" size="sm" className="h-8" onClick={() => openEditBooking(booking)} data-testid={`edit-pending-${booking.id}`}>
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button variant="outline" size="sm" className="h-8 text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30" onClick={() => setCancelBooking(booking)} data-testid={`cancel-pending-${booking.id}`}>
                        <XCircle className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  <PreferencesList preferences={booking.client_preferences} compact title="Le tue preferenze" testId={`pending-preferences-${booking.id}`} />
                  <ReferencePhotos booking={booking} headers={getAuthHeaders()} title="Le tue foto di riferimento" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative h-[80vh] min-h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={siteTexts?.heroImage || "https://images.unsplash.com/photo-1759134198561-e2041049419c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBiYXJiZXIlMjBzaG9wfGVufDB8fHx8MTc3MzIzMjczNnww&ixlib=rb-4.1.0&q=85"}
            alt="Modern barber shop"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/90 via-background/70 to-background" />
        </div>

        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="max-w-4xl mx-auto space-y-6">
            <h1 className="text-[2.2rem] sm:text-6xl lg:text-7xl font-extrabold tracking-wide whitespace-nowrap" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              {siteTexts?.heroTitle || 'CAPELLI & CAPELLI'}
            </h1>
            <p className="text-xl sm:text-2xl text-muted-foreground font-light">
              {siteTexts?.heroSubtitle || 'Di Evian • Sala Uomo'}
            </p>
            <p className="text-lg sm:text-xl text-foreground/90 max-w-2xl mx-auto">
              {siteTexts?.heroDescription || 'Stile, precisione e personalità'}
            </p>
            
            {client && (
              <p className="text-accent text-lg">
                Benvenuto, {client.first_name}!
              </p>
            )}
            
            <div className="pt-6">
              <Link to="/prenota">
                <Button size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8 py-6 font-semibold btn-premium">
                  {siteTexts?.bookingCta || 'PRENOTA ORA'}
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>

            <div className="pt-8 flex flex-wrap justify-center gap-6 sm:gap-8">
              {features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <div key={feature.title} className="flex items-center gap-2 text-sm sm:text-base">
                    <Icon className="h-5 w-5 text-accent" />
                    <span className="font-semibold text-foreground">{feature.title}</span>
                    <span className="text-muted-foreground">•</span>
                    <span className="text-muted-foreground">{feature.description}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">I Nostri Servizi</h2>
            <p className="text-muted-foreground text-lg">Scegli il servizio perfetto per il tuo stile</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {(dbServices.length > 0 ? dbServices : []).map((service, index) => (
              <Card key={service.id || index} className="card-elegant group cursor-pointer">
                <CardContent className="p-6 flex flex-col h-full">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 rounded-lg bg-accent/10 group-hover:bg-accent/20 transition-colors">
                      <Scissors className="h-6 w-6 text-accent" />
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-accent">&euro;{service.price}</p>
                      <p className="text-xs text-muted-foreground">{service.duration} min</p>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{service.name}</h3>
                  {service.description && <p className="text-sm text-muted-foreground mb-2">{service.description}</p>}
                  <div className="mt-auto pt-4">
                    <Link to="/prenota">
                      <Button variant="ghost" className="w-full justify-between group-hover:bg-accent/10">
                        Prenota
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/prenota">
              <Button size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90">
                Vedi Tutti i Servizi
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-secondary">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Perché Sceglierci</h2>
              <p className="text-muted-foreground text-lg">Esperienza e professionalità al tuo servizio</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="inline-flex p-4 rounded-full bg-accent/10 mb-4">
                  <Star className="h-8 w-8 text-accent" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Qualità Premium</h3>
                <p className="text-muted-foreground">Prodotti di alta qualità e tecniche moderne per risultati impeccabili</p>
              </div>

              <div className="text-center">
                <div className="inline-flex p-4 rounded-full bg-accent/10 mb-4">
                  <Scissors className="h-8 w-8 text-accent" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Esperienza</h3>
                <p className="text-muted-foreground">Specialisti in tagli maschili con anni di esperienza nel settore</p>
              </div>

              <div className="text-center">
                <div className="inline-flex p-4 rounded-full bg-accent/10 mb-4">
                  <Clock className="h-8 w-8 text-accent" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Flessibilità</h3>
                <p className="text-muted-foreground">Orari comodi e sistema di prenotazione online disponibile 24/7</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Pronto per un Nuovo Look?</h2>
          <p className="text-muted-foreground text-lg mb-8">Prenota il tuo appuntamento oggi stesso</p>
          <Link to="/prenota">
            <Button size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8 py-6 btn-premium">
              PRENOTA SUBITO
            </Button>
          </Link>
        </div>
      </section>

      {/* Edit Booking Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md" data-testid="edit-booking-dialog">
          <DialogHeader>
            <DialogTitle>Modifica Prenotazione</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Servizio</Label>
              <Select value={editForm.service_name || ''} onValueChange={(v) => setEditForm({ ...editForm, service_name: v })}>
                <SelectTrigger data-testid="edit-service-select"><SelectValue placeholder="Seleziona servizio" /></SelectTrigger>
                <SelectContent>
                  {dbServices.map((s) => (
                    <SelectItem key={s.id} value={s.name}>{s.name} - {s.price} EUR</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Operatore</Label>
              <Select value={editForm.operator_name || ''} onValueChange={(v) => setEditForm({ ...editForm, operator_name: v })}>
                <SelectTrigger data-testid="edit-operator-select"><SelectValue placeholder="Seleziona operatore" /></SelectTrigger>
                <SelectContent>
                  {operators.map((op) => (
                    <SelectItem key={op.id} value={op.name}>{op.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data</Label>
                <Input type="date" value={editForm.date || ''} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} data-testid="edit-date-input" />
              </div>
              <div>
                <Label>Ora</Label>
                <Input type="time" value={editForm.time || ''} onChange={(e) => setEditForm({ ...editForm, time: e.target.value })} data-testid="edit-time-input" />
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setEditOpen(false)} className="flex-1" data-testid="cancel-edit-btn">Annulla</Button>
              <Button onClick={handleSaveEdit} disabled={saving} className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="save-edit-btn">
                {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Salvataggio...</> : 'Salva Modifiche'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Booking Confirmation */}
      <AlertDialog open={!!cancelBooking} onOpenChange={(open) => { if (!open) setCancelBooking(null); }}>
        <AlertDialogContent data-testid="cancel-booking-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Annullare l'appuntamento?</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler annullare la prenotazione per <strong>{cancelBooking?.service_name}</strong> del{' '}
              <strong>{cancelBooking?.date}</strong> alle <strong>{cancelBooking?.time}</strong>?
              <br /><br />Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="keep-booking-btn">Torna Indietro</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancelBooking} disabled={cancelling}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-cancel-booking-btn">
              {cancelling ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Annullamento...</> : <><XCircle className="h-4 w-4 mr-1" /> Annulla Appuntamento</>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default HomePage;
